import type { ApiResponse } from "@shared/contracts";
import { useCustomMutation, type WriteApiOptions } from "@shared/api/hooks";
import { api, ApiError } from "@shared/api";
import { AUTH_LOGIN_SERVICE_NAME } from "../constants";
import type { Login, LoginResult } from "../types";

export type UseLoginApiOptions = Omit<
  WriteApiOptions<LoginResult, Login>,
  "serviceName" | "idRequired" | "invalidateKeys"
>;

/**
 * Log in — `POST /v1/{actor}/login`, resolves `{ user, token, refresh_token }`.
 * Persisting the session is the auth store's job (via `onSuccess`).
 *
 * Unlike the other write hooks this does not go through `usePostApi`, because
 * a failed login is **not** an HTTP error: `BaseAuthController::login()` calls
 * `rest()->message(...)->send()` with no status setter, so bad credentials come
 * back as `{ data: null, message: "Credentials not match records", code: 200 }`.
 * Success is therefore `data !== null`, not the status — and the check lives
 * here rather than in the transport, where a null `data` is a perfectly
 * ordinary success (logout, reset-password, every `noData()` branch).
 *
 * The public surface is unchanged: it still resolves the envelope and still
 * rejects with an `ApiError`, so the error banner and toast pipeline work as
 * they do everywhere else. Delete the guard once login returns a 401.
 *
 * @example
 * ```typescript
 * const login = useLoginApi({
 *   onSuccess: (session) => setSession(session),
 * });
 * login.mutate({ email, password });
 * ```
 */
export function useLoginApi(options?: UseLoginApiOptions) {
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useCustomMutation<ApiResponse<LoginResult>, ApiError, Login>({
    ...mutationOptions,
    mutationFn: async (body) => {
      const response = await api.post<LoginResult | null>(
        AUTH_LOGIN_SERVICE_NAME,
        body
      );

      if (!response.data?.token) {
        throw ApiError.businessFailure(response, AUTH_LOGIN_SERVICE_NAME);
      }

      return response as ApiResponse<LoginResult>;
    },
    onSuccess: (response, variables) => {
      onSuccess?.(response.data, response.message, variables);
    },
  });
}
